/**
 * @module scroll-effects
 * Handles scroll-triggered behaviors using IntersectionObserver.
 * Replicates core functionality of Locomotive Scroll data attributes:
 * - data-scroll-call: Function to call.
 * - data-scroll-repeat: If present, triggers 'exit' event and re-triggers 'enter'.
 * - data-scroll-offset: "top,bottom" or single value. Maps to rootMargin.
 */

const observers = new Map();

/**
 * Handles changing the body's background color attribute based on section visibility.
 */
function sectionBgColour(way, el) {
    if (way === 'enter') {
        const styleSet = el.getAttribute('data-style-set');
        if (styleSet) {
            document.body.setAttribute('data-bgcolor', styleSet);
        }
    }
}

/**
 * Toggles the 'is-inview' class on elements.
 */
function layoutInView(way, el) {
    if (way === 'enter') {
        el.classList.add('is-inview');
    } else if (way === 'exit') { // if repeat
        el.classList.remove('is-inview');
    }
}

/**
 * Staggers the 'is-inview' class on child elements.
 * Usage:
 * - data-scroll-call="staggerInView"
 * - data-stagger-selector=".child-class" (optional, defaults to direct children)
 * - data-stagger-delay="0.1" (optional, delay in seconds, defaults to 0.1)
 */
// Queue for staggered items
let staggerQueue = [];
let staggerTimer = null;

function processStaggerQueue() {
    if (staggerQueue.length === 0) return;

    // Sort by DOM order to ensure visual consistency
    // Note: detailed sort might be expensive for huge lists, but necessary for correctness
    staggerQueue.sort((a, b) => {
        return (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) ? -1 : 1;
    });

    // Determine delay step from the first element's attribute or default to 0.1s
    const firstEl = staggerQueue[0];
    const delayStep = firstEl.getAttribute('data-stagger-delay') 
        ? parseFloat(firstEl.getAttribute('data-stagger-delay')) * 1000 // Convert to ms
        : 100;

    staggerQueue.forEach((el, index) => {
        const delay = index * delayStep;
        
        // Use JS timeout to add the class, allowing standard CSS transitions to take over immediately
        setTimeout(() => {
            el.classList.add('is-inview');
        }, delay);
    });

    staggerQueue = [];
    staggerTimer = null;
}

/**
 * Adds an element to the staggered queue to be animated into view.
 * Usage on individual items: data-scroll-call="staggeredItemInView"
 */
function staggeredItemInView(way, el) {
    if (way === 'enter') {
        if (!el.classList.contains('is-inview')) {
            staggerQueue.push(el);
            
            // Debounce the processing so we catch all items in this frame/scroll tick
            if (!staggerTimer) {
                staggerTimer = setTimeout(processStaggerQueue, 50);
            }
        }
    } else if (way === 'exit') {
        // Optional: remove class on exit if repeat is on
        el.classList.remove('is-inview');
    }
}

/**
 * Legacy/Wrapper Stagger
 * Tries to find children and animate them. 
 * Kept for backwards compatibility but 'staggeredItemInView' on items is preferred for ALM.
 */
function staggerInView(way, el) {
    const selector = el.getAttribute('data-stagger-selector');
    const items = selector ? el.querySelectorAll(selector) : el.children;
    const delayStep = parseFloat(el.getAttribute('data-stagger-delay')) || 0.1;

    if (way === 'enter') {
        // Filter out items that are already in view
        const newItems = Array.from(items).filter(item => !item.classList.contains('is-inview'));
        
        newItems.forEach((item, index) => {
            item.style.setProperty('--delay', `${index * delayStep}s`);
            item.style.transitionDelay = `${index * delayStep}s`;
            item.classList.add('is-inview');
        });
        el.classList.add('is-inview');
    } else if (way === 'exit') {
        Array.from(items).forEach(item => {
            item.style.removeProperty('--delay');
            item.style.transitionDelay = '';
            item.classList.remove('is-inview');
        });
        el.classList.remove('is-inview');
    }
}

// Map alias for consistency if needed, but the logic handles repeat via attribute now.
const scrollCallFunctions = {
    sectionBgColour,
    layoutInView,
    staggerInView,
    staggeredItemInView
};

/**
 * Callback for IntersectionObserver
 */
function observerCallback(entries, observer) {
    entries.forEach(entry => {
        const el = entry.target;
        const funcs = el.getAttribute('data-scroll-call').split(' ');
        const repeat = el.hasAttribute('data-scroll-repeat');
        
        let way = '';
        const isPastTrigger = entry.rootBounds && (entry.boundingClientRect.top < entry.rootBounds.top);
        
        if (entry.isIntersecting || isPastTrigger) {
            way = 'enter';
        } else if (repeat) {
            way = 'exit';
        }

        if (way) {
            funcs.forEach(funcName => {
                if (scrollCallFunctions[funcName]) {
                    scrollCallFunctions[funcName](way, el);
                }
            });
        }

        // If entering and no repeat, stop observing
        if (way === 'enter' && !repeat) {
            observer.unobserve(el);
        }
    });
}

/**
 * Maps "bottom,top" offsets to CSS rootMargin.
 * Input "10%,90%" -> Output "-90% 0px -10% 0px"
 */
function getRootMargin(offsetAttr) {
    const defaults = ["10%", "90%"];
    const parts = offsetAttr ? offsetAttr.split(',') : defaults;

    // Map parts and ensure they have units
    const formatValue = (val) => {
        let v = val?.trim() || "0";
        if (!isNaN(v)) v += 'px'; // Add px if it's a raw number
        
        // Prefix with '-' to move the margin "inwards" if not already zero/negative
        return (v !== "0" && !v.startsWith('-')) ? `-${v}` : v;
    };

    const bottom = formatValue(parts[0]);
    const top = formatValue(parts[1] || defaults[1]); // Fallback to first value if second is missing

    // rootMargin order: top, right, bottom, left
    return `${top} 0px ${bottom} 0px`;
}

/**
 * Initializes the scroll effects.
 */
export function initializeScrollEffects() {
    destroyScrollEffects();

    const elements = document.querySelectorAll('[data-scroll-call]');
    
    // Group elements by their computed rootMargin to minimize Observer instances
    const groups = new Map();

    elements.forEach(el => {
        const offsetAttr = el.getAttribute('data-scroll-offset');
        const margin = getRootMargin(offsetAttr);
        
        if (!groups.has(margin)) {
            groups.set(margin, []);
        }
        groups.get(margin).push(el);
    });

    // Create observers
    groups.forEach((els, margin) => {
        const observer = new IntersectionObserver(observerCallback, {
            root: null, // viewport
            rootMargin: margin,
            threshold: 0
        });

        els.forEach(el => observer.observe(el));
        observers.set(observer, els);
    });
}

/**
 * Cleans up all observers.
 */
export function destroyScrollEffects() {
    observers.forEach((els, observer) => {
        observer.disconnect();
    });
    observers.clear();
}